import {
  formatResolution,
  getCameraTypeShortName,
  getMegapixels,
} from '@/lib/cameraSlug';

/** The figures a camera page's title, description and H1 are made of. */
export interface SeoFigures {
  manufacturer: string;
  model: string;
  resolution?: string;
  type: string;
  hfov: number;
  range?: number;
  irRange: number;
}

/**
 * A head the live site serves, kept word for word: the title and description without their fixed endings (`s: 0`
 * when the page had none), and the H1 where it is not the brand and the title's model.
 */
export interface FrozenHead {
  t: string;
  d: string;
  h?: string;
  s?: 0;
}

export const TITLE_END = ' | Free Coverage Planner';
export const DESCRIPTION_END = ' Free interactive coverage planner — no signup. Plan your CCTV layout in 2 min.';
const MODEL_END = ' Specs & FOV — ';

/** The title, description, og:title and H1 a frozen head stands for. */
export function frozenSeoHead(f: FrozenHead, manufacturer: string) {
  return {
    title: f.s === 0 ? f.t : f.t + TITLE_END,
    ogTitle: f.t,
    description: f.s === 0 ? f.d : f.d + DESCRIPTION_END,
    h1: f.h ?? `${manufacturer} ${f.t.split(MODEL_END)[0]}`,
  };
}

/** Sites that republish datasheets: a link there is not the maker's own word. */
const NOT_THE_MAKER = /(^|\.)(sourcesecurity\.com|ipvm\.com|jvsg\.com|amazon\.[a-z.]+|ebay\.[a-z.]+|aliexpress\.com|alibaba\.com)$/i;

/** The maker's own page the figures were read from, when the page has the maker's figures. */
export function makerSourceUrl(c: { datasheetUrl?: string; figureSource?: string }): string | undefined {
  if (c.figureSource || !c.datasheetUrl) return undefined;
  const host = c.datasheetUrl.match(/^https?:\/\/(?:web\.archive\.org\/web\/\d+(?:id_)?\/https?:\/\/)?([^/:]+)/i)?.[1];
  // a few makers put spaces in their addresses ("IP Camera & NVR"): a link needs them encoded
  return host && !NOT_THE_MAKER.test(host) ? c.datasheetUrl.replace(/ /g, '%20') : undefined;
}

const TITLE_MAX = 65;

/**
 * Title, description, og:title and H1 of a camera page.
 *
 * Almost every search that shows a camera page is the bare model code ("ds-2cd1143g3-liu": 94% of the named
 * impressions, Search Console 28 days to 2026-10-16), at positions 3-10 with a click rate under 1%: the searcher
 * wants the camera's datasheet figures. So the title leads with brand and code, says "Datasheet" where the
 * figures are the maker's own (and its page is linked), then the figures that decide a choice; the description
 * repeats them and says where they come from. Pages Google ranks keep their own head (CAMERA_SEO_FROZEN).
 */
export function cameraSeoHead(c: SeoFigures & { datasheetUrl?: string; figureSource?: string; focalLength?: string }) {
  const mp = getMegapixels(c.resolution);
  const typeShort = getCameraTypeShortName(c.type, c.model);
  // Brand is redundant when the model name already contains it (Axis/Hikvision
  // SKUs frequently embed the brand). Keep the model verbatim in that case.
  const brandInModel = c.model.toLowerCase().includes(c.manufacturer.toLowerCase());
  const modelTitle = brandInModel ? c.model : `${c.manufacturer} ${c.model}`;
  const maker = makerSourceUrl(c);
  const lead = `${modelTitle} ${maker ? 'Datasheet' : 'Specs'}: `;
  // under 0.5 MP is a thermal imager's frame (384×288, 640×512): its size says more than "0.1MP"
  const thermal = mp > 0 && mp < 0.5;
  const size = thermal ? `${c.resolution!.replace(/\s*[x×]\s*/, '×')} thermal ${typeShort}` : mp ? `${mp}MP ${typeShort}` : typeShort;
  const figures = [size, `${c.hfov}° FOV`, c.irRange ? `${c.irRange}m IR` : ''].filter(Boolean);
  let title = lead + figures.join(', ');
  while (title.length > TITLE_MAX && figures.length > 1) { figures.pop(); title = lead + figures.join(', '); }
  const parts = [
    thermal ? `${size} camera` : mp ? `${formatResolution(c.resolution)} ${typeShort} camera` : `${typeShort} camera`,
    `${c.hfov}° horizontal field of view`,
    c.focalLength ? `${c.focalLength} lens` : '',
    c.irRange ? `${c.irRange} m IR` : '',
  ].filter(Boolean);
  const source = maker ? ` From ${c.manufacturer}'s own product page.` : '';
  return {
    title,
    ogTitle: title,
    description: `${modelTitle}: ${parts.join(', ')}.${source} Plan its coverage free.`,
    h1: `${c.manufacturer} ${c.model}`,
  };
}
